import type { GameId } from "../shared/games";
import type { PlayerSlot, PlayerStatus, Point } from "../shared/messages";
import { WORD_HUNT_WORDS } from "../shared/wordhuntWords";

export type ArcadeGameId = Exclude<GameId, "minigolf">;

export type ArcadeEvent =
  | {
      gameId: "eightball";
      round: number;
      kind: "eightball_shot";
      shooterId: string;
      start: Point;
      velocity: Point;
    }
  | {
      gameId: "cuppong";
      round: number;
      kind: "cuppong_shot";
      shooterId: string;
      start: Point;
      target: Point;
      hitCupId?: string;
      rimCupId?: string;
      score: number;
    }
  | {
      gameId: "wordhunt";
      round: number;
      kind: "wordhunt_word";
      playerId: string;
      word: string;
      points: number;
      total: number;
      path: number[];
    };

export type ArcadeContext = {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  localPlayerId: string;
  localSlot?: PlayerSlot;
  players: PlayerStatus[];
  phase?: string;
  sendEvent: (event: ArcadeEvent) => void;
  updateScore: (score: number, complete?: boolean) => void;
  playTap: (volume?: number) => void;
};

type ArcadePointer =
  | { type: "idle" }
  | { type: "aim"; id: number; start: Point; current: Point; subject: "cue" | "pong" }
  | { type: "word"; id: number; path: number[] };

type Layout = {
  x: number;
  y: number;
  w: number;
  h: number;
  scale: number;
};

type PoolBall = {
  id: string;
  label: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  color: string;
  stripe?: boolean;
  pocketed?: boolean;
};

type EightBallState = {
  balls: PoolBall[];
  pocketed: string[];
  lastShotAt: number;
};

type Cup = {
  id: string;
  x: number;
  y: number;
  active: boolean;
};

type PongShot = {
  start: Point;
  target: Point;
  startedAt: number;
  duration: number;
  color: string;
  shooterId: string;
  hitCupId?: string;
  rimCupId?: string;
  bounceAngle: number;
};

type CupPongState = {
  cups: Cup[];
  shots: PongShot[];
};

type WordCell = {
  index: number;
  letter: string;
  x: number;
  y: number;
};

type WordFeedback = {
  word: string;
  title: string;
  detail: string;
  kind: "success" | "warning" | "error";
  points?: number;
  at: number;
};

type WordHuntState = {
  boardIndex: number;
  cells: WordCell[];
  found: Set<string>;
  foundOrder: string[];
  remoteFound: Array<{ word: string; playerId: string; at: number }>;
  lastWord?: { word: string; points: number; at: number };
  lastFeedback?: WordFeedback;
  startedAt: number;
  durationMs: number;
};

type Session = {
  gameId: ArcadeGameId;
  seed: string;
  round: number;
  key: string;
  score: number;
  completed: boolean;
  message: string;
  pointer: ArcadePointer;
  eightball?: EightBallState;
  cuppong?: CupPongState;
  wordhunt?: WordHuntState;
};

type ArcadeGameModule = {
  id: ArcadeGameId;
  create: (seed: string, round: number, key: string) => Session;
  update: (session: Session, now: number, delta: number) => void;
  render: (session: Session) => void;
  pointerDown: (session: Session, pointerId: number, point: Point) => boolean;
  pointerMove: (session: Session, pointerId: number, point: Point) => boolean;
  pointerUp: (session: Session, pointerId: number, point: Point) => boolean;
  applyRemoteEvent: (session: Session, event: ArcadeEvent, playerId: string) => void;
  debug: (session: Session) => unknown;
};

const VIRTUAL_W = 1000;
const VIRTUAL_H = 700;
const POOL_W = 860;
const POOL_H = 500;
const BALL_R = 22;
const POOL_RAIL = 34;
const POOL_FRICTION = 0.982;
const POOL_REST_SPEED = 0.045;
const POOL_CUSHION_RESTITUTION = 0.9;
const POOL_POCKET_VISUAL_RADIUS = 44;
const POOL_POCKET_CAPTURE_RADIUS = 36;
const POOL_POCKET_MOUTH_RADIUS = POOL_POCKET_VISUAL_RADIUS + BALL_R * 0.7;
const PONG_DURATION = 1080;
const WORD_ROUND_MS = 75_000;
const WORD_POINTS: Record<number, number> = {
  3: 100,
  4: 400,
  5: 800,
  6: 1400,
};

const SLOT_COLORS: Record<PlayerSlot, string> = {
  A: "#ffffff",
  B: "#6de5ff",
  C: "#ffd45c",
  D: "#ff7ac8",
};

const WORD_BOARDS = [
  ["T", "E", "A", "M", "R", "A", "T", "E", "S", "O", "N", "G", "L", "I", "N", "E"],
  ["P", "L", "A", "Y", "T", "O", "N", "E", "S", "H", "A", "R", "E", "D", "G", "O"],
  ["C", "A", "R", "D", "O", "P", "E", "N", "M", "I", "N", "I", "G", "A", "M", "E"],
  ["B", "A", "L", "L", "R", "A", "I", "L", "S", "H", "O", "T", "C", "U", "P", "S"],
  ["H", "A", "T", "E", "A", "I", "L", "S", "R", "O", "N", "E", "C", "D", "G", "P"],
];

const WORDS: ReadonlySet<string> = new Set<string>(WORD_HUNT_WORDS);

export function isArcadeGameId(gameId: GameId): gameId is ArcadeGameId {
  return gameId === "eightball" || gameId === "cuppong" || gameId === "wordhunt";
}

export class ArcadeRuntime {
  private session: Session | undefined;
  private players: PlayerStatus[] = [];
  private readonly modules: Record<ArcadeGameId, ArcadeGameModule>;

  constructor(private readonly context: () => ArcadeContext) {
    this.modules = {
      eightball: {
        id: "eightball",
        create: (seed, round, key) => {
          const session = this.createBaseSession("eightball", seed, round, key, "Drag from the cue ball");
          session.eightball = this.createEightBall(seed, round);
          return session;
        },
        update: (session, _now, delta) => this.updateEightBall(session, delta),
        render: (session) => this.drawEightBall(session),
        pointerDown: (session, pointerId, point) => this.pointerDownEightBall(session, pointerId, point),
        pointerMove: (session, pointerId, point) => this.moveAimPointer(session, pointerId, point),
        pointerUp: (session, pointerId, point) => this.releaseAimPointer(session, pointerId, point, () => this.releaseEightBallShot(session)),
        applyRemoteEvent: (session, event, playerId) => this.applyEightBallEvent(session, event, playerId),
        debug: (session) => this.debugEightBall(session),
      },
      cuppong: {
        id: "cuppong",
        create: (seed, round, key) => {
          const session = this.createBaseSession("cuppong", seed, round, key, "Pull down, release");
          session.cuppong = this.createCupPong();
          return session;
        },
        update: (session, now) => this.updateCupPong(session, now),
        render: (session) => this.drawCupPong(session),
        pointerDown: (session, pointerId, point) => this.pointerDownCupPong(session, pointerId, point),
        pointerMove: (session, pointerId, point) => this.moveAimPointer(session, pointerId, point),
        pointerUp: (session, pointerId, point) => this.releaseAimPointer(session, pointerId, point, () => this.releaseCupPongShot(session)),
        applyRemoteEvent: (session, event, playerId) => this.applyCupPongEvent(session, event, playerId),
        debug: (session) => this.debugCupPong(session),
      },
      wordhunt: {
        id: "wordhunt",
        create: (seed, round, key) => {
          const session = this.createBaseSession("wordhunt", seed, round, key, "Swipe words");
          session.wordhunt = this.createWordHunt(seed, round);
          return session;
        },
        update: (session, now) => this.updateWordHunt(session, now),
        render: (session) => this.drawWordHunt(session),
        pointerDown: (session, pointerId, point) => this.pointerDownWordHunt(session, pointerId, point),
        pointerMove: (session, pointerId, point) => this.moveWordPointer(session, pointerId, point),
        pointerUp: (session, pointerId) => this.releaseWordPointer(session, pointerId),
        applyRemoteEvent: (session, event, playerId) => this.applyWordHuntEvent(session, event, playerId),
        debug: (session) => this.debugWordHunt(session),
      },
    };
  }

  sync(gameId: ArcadeGameId, seed: string, round: number, players: PlayerStatus[]): void {
    this.players = players;
    const key = `${gameId}:${seed}:${round}`;
    if (this.session?.key === key) return;
    this.session = this.moduleFor(gameId).create(seed, round, key);
    this.context().updateScore(0, false);
  }

  update(now: number, delta: number): void {
    const session = this.session;
    if (!session || this.context().phase !== "playing") return;
    this.moduleFor(session.gameId).update(session, now, delta);
  }

  render(): void {
    const session = this.session;
    const { canvas, ctx } = this.context();
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);
    this.drawArcadeBackdrop(ctx, rect.width, rect.height, session?.gameId ?? "eightball");
    if (!session) return;
    this.moduleFor(session.gameId).render(session);
  }

  onPointerDown(event: PointerEvent): boolean {
    const session = this.session;
    if (!session || this.context().phase !== "playing") return false;
    event.preventDefault();
    const point = this.eventPoint(event);
    return this.moduleFor(session.gameId).pointerDown(session, event.pointerId, point);
  }

  onPointerMove(event: PointerEvent): boolean {
    const session = this.session;
    if (!session || session.pointer.type === "idle") return false;
    event.preventDefault();
    const point = this.eventPoint(event);
    return this.moduleFor(session.gameId).pointerMove(session, event.pointerId, point);
  }

  onPointerUp(event: PointerEvent): boolean {
    const session = this.session;
    if (!session || session.pointer.type === "idle") return false;
    event.preventDefault();
    const point = this.eventPoint(event);
    return this.moduleFor(session.gameId).pointerUp(session, event.pointerId, point);
  }

  applyRemoteEvent(event: ArcadeEvent, playerId: string): void {
    const session = this.session;
    if (!session || event.round !== session.round || event.gameId !== session.gameId) return;
    this.moduleFor(session.gameId).applyRemoteEvent(session, event, playerId);
  }

  debug(): unknown {
    const session = this.session;
    if (!session) return null;
    return {
      gameId: session.gameId,
      round: session.round,
      score: session.score,
      completed: session.completed,
      message: session.message,
      pointer: session.pointer.type,
      module: this.moduleFor(session.gameId).debug(session),
    };
  }

  private createBaseSession(gameId: ArcadeGameId, seed: string, round: number, key: string, message: string): Session {
    return {
      gameId,
      seed,
      round,
      key,
      score: 0,
      completed: false,
      message,
      pointer: { type: "idle" },
    };
  }

  private moduleFor(gameId: ArcadeGameId): ArcadeGameModule {
    return this.modules[gameId];
  }

  private moveAimPointer(session: Session, pointerId: number, point: Point): boolean {
    if (session.pointer.type !== "aim" || session.pointer.id !== pointerId) return false;
    session.pointer.current = point;
    return true;
  }

  private releaseAimPointer(session: Session, pointerId: number, point: Point, release: () => void): boolean {
    if (session.pointer.type !== "aim" || session.pointer.id !== pointerId) return false;
    session.pointer.current = point;
    release();
    session.pointer = { type: "idle" };
    return true;
  }

  private moveWordPointer(session: Session, pointerId: number, point: Point): boolean {
    if (session.pointer.type !== "word" || session.pointer.id !== pointerId) return false;
    this.extendWordPath(session, point);
    return true;
  }

  private releaseWordPointer(session: Session, pointerId: number): boolean {
    if (session.pointer.type !== "word" || session.pointer.id !== pointerId) return false;
    this.releaseWordPath(session);
    session.pointer = { type: "idle" };
    return true;
  }

  private applyEightBallEvent(session: Session, event: ArcadeEvent, playerId: string): void {
    if (event.kind !== "eightball_shot") return;
    const cue = this.cueBall(session);
    if (!cue) return;
    cue.x = event.start.x;
    cue.y = event.start.y;
    cue.vx = event.velocity.x;
    cue.vy = event.velocity.y;
    session.message = `${this.playerName(playerId)} breaks`;
  }

  private applyCupPongEvent(session: Session, event: ArcadeEvent, playerId: string): void {
    if (event.kind !== "cuppong_shot") return;
    const rimCupId = event.rimCupId ?? this.pongRimCupAtTarget(session, event.target, event.hitCupId)?.id;
    this.addPongShot(session, event.start, event.target, event.hitCupId, rimCupId, this.playerColor(playerId), playerId);
    if (event.hitCupId) {
      const cup = session.cuppong?.cups.find((entry) => entry.id === event.hitCupId);
      if (cup) cup.active = false;
    }
    session.message = event.hitCupId ? `${this.playerName(playerId)} sinks one` : `${this.playerName(playerId)} just missed`;
  }

  private applyWordHuntEvent(session: Session, event: ArcadeEvent, playerId: string): void {
    if (event.kind !== "wordhunt_word") return;
    session.wordhunt?.remoteFound.unshift({ word: event.word, playerId, at: performance.now() });
    if (session.wordhunt && session.wordhunt.remoteFound.length > 5) session.wordhunt.remoteFound.pop();
    session.message = `${this.playerName(playerId)} found ${event.word}`;
  }

  private debugEightBall(session: Session): unknown {
    if (!session.eightball) return undefined;
    return {
      balls: session.eightball.balls.map((ball) => ({
        id: ball.id,
        x: ball.x,
        y: ball.y,
        vx: ball.vx,
        vy: ball.vy,
        pocketed: Boolean(ball.pocketed),
      })),
      pool: {
        width: POOL_W,
        height: POOL_H,
        rail: POOL_RAIL,
        ballRadius: BALL_R,
        pocketVisualRadius: POOL_POCKET_VISUAL_RADIUS,
        pocketCaptureRadius: POOL_POCKET_CAPTURE_RADIUS,
        pocketMouthRadius: POOL_POCKET_MOUTH_RADIUS,
        pockets: this.poolPockets(),
      },
      moving: this.poolMoving(session),
    };
  }

  private debugCupPong(session: Session): unknown {
    if (!session.cuppong) return undefined;
    const activeShot = session.cuppong.shots[0];
    return {
      cups: session.cuppong.cups.map((cup) => ({ id: cup.id, x: cup.x, y: cup.y, active: cup.active })),
      cupsLeft: session.cuppong.cups.filter((cup) => cup.active).length,
      ballStart: this.pongBallStart(),
      table: { x: 100, y: 45, w: 800, h: 610 },
      shots: session.cuppong.shots.length,
      activeShot: activeShot
        ? {
            shooterId: activeShot.shooterId,
            target: activeShot.target,
            hitCupId: activeShot.hitCupId,
            rimCupId: activeShot.rimCupId,
            bounceAngle: activeShot.bounceAngle,
          }
        : undefined,
    };
  }

  private debugWordHunt(session: Session): unknown {
    if (!session.wordhunt) return undefined;
    return {
      found: Array.from(session.wordhunt.found),
      foundOrder: session.wordhunt.foundOrder,
      boardIndex: session.wordhunt.boardIndex,
      cells: session.wordhunt.cells.map((cell) => ({ index: cell.index, letter: cell.letter, x: cell.x, y: cell.y })),
      lastWord: session.wordhunt.lastWord
        ? {
            word: session.wordhunt.lastWord.word,
            points: session.wordhunt.lastWord.points,
          }
        : undefined,
      lastFeedback: session.wordhunt.lastFeedback
        ? {
            word: session.wordhunt.lastFeedback.word,
            title: session.wordhunt.lastFeedback.title,
            detail: session.wordhunt.lastFeedback.detail,
            kind: session.wordhunt.lastFeedback.kind,
            points: session.wordhunt.lastFeedback.points,
          }
        : undefined,
      currentWord: this.currentWord(session),
      wordCount: WORDS.size,
      dictionaryChecks: {
        HAT: WORDS.has("HAT"),
        HAIL: WORDS.has("HAIL"),
        BAIL: WORDS.has("BAIL"),
      },
      remainingMs: this.wordRemainingMs(session),
    };
  }

  private createEightBall(seed: string, round: number): EightBallState {
    const colors = ["#f5c542", "#4fa2ff", "#ec5aa4", "#6de28b", "#ff7d43", "#a77cff", "#54d6d8"];
    const balls: PoolBall[] = [
      { id: "cue", label: "", x: 205, y: POOL_H / 2, vx: 0, vy: 0, r: BALL_R, color: "#ffffff" },
    ];
    const rackLabels = ["1", "2", "3", "4", "8", "5", "6", "7", "9", "10"];
    let rackIndex = 0;
    for (let row = 0; row < 4; row += 1) {
      for (let col = 0; col <= row; col += 1) {
        const label = rackLabels[rackIndex] ?? String(rackIndex + 1);
        const x = 595 + row * BALL_R * 1.86;
        const y = POOL_H / 2 + (col - row / 2) * BALL_R * 2.08;
        balls.push({
          id: label,
          label,
          x,
          y,
          vx: 0,
          vy: 0,
          r: BALL_R,
          color: label === "8" ? "#111318" : colors[rackIndex % colors.length],
          stripe: Number(label) > 8,
        });
        rackIndex += 1;
      }
    }
    const jitter = seededRandom(`${seed}-${round}`)() * 0.8 - 0.4;
    balls.forEach((ball, index) => {
      if (ball.id !== "cue") ball.y += Math.sin(index * 2.4) * jitter * 4;
    });
    return { balls, pocketed: [], lastShotAt: 0 };
  }

  private createCupPong(): CupPongState {
    const cups: Cup[] = [];
    const startX = VIRTUAL_W / 2;
    let id = 0;
    for (let row = 0; row < 4; row += 1) {
      for (let col = 0; col <= row; col += 1) {
        cups.push({
          id: `cup-${id}`,
          x: startX + (col - row / 2) * 72,
          y: 140 + row * 58,
          active: true,
        });
        id += 1;
      }
    }
    return { cups, shots: [] };
  }

  private createWordHunt(seed: string, round: number): WordHuntState {
    const boardIndex = this.forcedWordBoardIndex() ?? Math.floor(seededRandom(`${seed}:${round}:words`)() * WORD_BOARDS.length) % WORD_BOARDS.length;
    const letters = WORD_BOARDS[boardIndex];
    const cells = letters.map((letter, index) => {
      const col = index % 4;
      const row = Math.floor(index / 4);
      return {
        index,
        letter,
        x: 255 + col * 164,
        y: 160 + row * 112,
      };
    });
    return { boardIndex, cells, found: new Set(), foundOrder: [], remoteFound: [], startedAt: performance.now(), durationMs: WORD_ROUND_MS };
  }

  private updateEightBall(session: Session, delta: number): void {
    const table = session.eightball;
    if (!table) return;
    const step = Math.min(2, Math.max(0.6, delta / 16.667));
    for (let tick = 0; tick < step; tick += 1) {
      for (const ball of table.balls) {
        if (ball.pocketed) continue;
        ball.x += ball.vx;
        ball.y += ball.vy;
        ball.vx *= POOL_FRICTION;
        ball.vy *= POOL_FRICTION;
        if (Math.hypot(ball.vx, ball.vy) < POOL_REST_SPEED) {
          ball.vx = 0;
          ball.vy = 0;
        }
        if (this.capturePoolBall(session, ball)) continue;
        if (this.inPoolPocketMouth(ball)) continue;
        const minX = POOL_RAIL + ball.r;
        const maxX = POOL_W - POOL_RAIL - ball.r;
        const minY = POOL_RAIL + ball.r;
        const maxY = POOL_H - POOL_RAIL - ball.r;
        if (ball.x < minX) {
          ball.x = minX;
          if (ball.vx < 0) ball.vx = Math.abs(ball.vx) * POOL_CUSHION_RESTITUTION;
        }
        if (ball.x > maxX) {
          ball.x = maxX;
          if (ball.vx > 0) ball.vx = -Math.abs(ball.vx) * POOL_CUSHION_RESTITUTION;
        }
        if (ball.y < minY) {
          ball.y = minY;
          if (ball.vy < 0) ball.vy = Math.abs(ball.vy) * POOL_CUSHION_RESTITUTION;
        }
        if (ball.y > maxY) {
          ball.y = maxY;
          if (ball.vy > 0) ball.vy = -Math.abs(ball.vy) * POOL_CUSHION_RESTITUTION;
        }
      }
      this.resolvePoolCollisions(table.balls);
      this.checkPockets(session);
    }
  }

  private resolvePoolCollisions(balls: PoolBall[]): void {
    for (let i = 0; i < balls.length; i += 1) {
      const a = balls[i];
      if (a.pocketed) continue;
      for (let j = i + 1; j < balls.length; j += 1) {
        const b = balls[j];
        if (b.pocketed) continue;
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const dist = Math.hypot(dx, dy) || 1;
        const minDist = a.r + b.r;
        if (dist >= minDist) continue;
        const nx = dx / dist;
        const ny = dy / dist;
        const overlap = (minDist - dist) / 2;
        a.x -= nx * overlap;
        a.y -= ny * overlap;
        b.x += nx * overlap;
        b.y += ny * overlap;
        const approachSpeed = (a.vx - b.vx) * nx + (a.vy - b.vy) * ny;
        if (approachSpeed <= 0) continue;
        const impulse = approachSpeed * 0.96;
        a.vx -= impulse * nx;
        a.vy -= impulse * ny;
        b.vx += impulse * nx;
        b.vy += impulse * ny;
      }
    }
  }

  private inPoolPocketMouth(ball: PoolBall): boolean {
    return this.poolPockets().some((entry) => distance(ball, entry) < POOL_POCKET_MOUTH_RADIUS);
  }

  private checkPockets(session: Session): void {
    const table = session.eightball;
    if (!table) return;
    for (const ball of table.balls) {
      if (ball.pocketed) continue;
      this.capturePoolBall(session, ball);
    }
  }

  private capturePoolBall(session: Session, ball: PoolBall): boolean {
    const table = session.eightball;
    if (!table || ball.pocketed) return false;
    const pocket = this.poolPockets().find((entry) => distance(ball, entry) < POOL_POCKET_CAPTURE_RADIUS);
    if (!pocket) return false;
    if (ball.id === "cue") {
      ball.x = 205;
      ball.y = POOL_H / 2;
      ball.vx = 0;
      ball.vy = 0;
      session.message = "Scratch. Cue reset.";
      return true;
    }
    ball.pocketed = true;
    ball.vx = 0;
    ball.vy = 0;
    table.pocketed.push(ball.id);
    session.score += ball.id === "8" ? 3 : 1;
    session.message = ball.id === "8" ? "8 ball down" : `Pocketed ${ball.label}`;
    this.context().playTap(0.055);
    this.context().updateScore(session.score, false);
    if (ball.id === "8" || table.pocketed.length >= table.balls.length - 1) {
      this.completeSession(session);
    }
    return true;
  }

  private updateCupPong(session: Session, now: number): void {
    const game = session.cuppong;
    if (!game) return;
    const activeShots: PongShot[] = [];
    for (const shot of game.shots) {
      const progress = (now - shot.startedAt) / shot.duration;
      if (progress < 1) {
        activeShots.push(shot);
        continue;
      }
      if (shot.hitCupId) {
        const cup = game.cups.find((entry) => entry.id === shot.hitCupId);
        if (cup) cup.active = false;
      }
    }
    game.shots = activeShots;
  }

  private updateWordHunt(session: Session, now: number): void {
    if (!session.wordhunt || session.completed) return;
    if (now - session.wordhunt.startedAt >= session.wordhunt.durationMs) {
      session.message = "Time";
      this.completeSession(session);
    }
  }

  private pointerDownEightBall(session: Session, id: number, point: Point): boolean {
    const cue = this.cueBall(session);
    if (!cue || this.poolMoving(session)) return false;
    const layout = this.layout(POOL_W, POOL_H);
    const cueScreen = toScreen(cue, layout);
    if (distance(point, cueScreen) > 56) return false;
    session.pointer = { type: "aim", id, start: cueScreen, current: point, subject: "cue" };
    return true;
  }

  private pointerDownCupPong(session: Session, id: number, point: Point): boolean {
    if ((session.cuppong?.shots ?? []).some((shot) => shot.shooterId === this.context().localPlayerId)) return false;
    const layout = this.layout(VIRTUAL_W, VIRTUAL_H);
    const ball = toScreen(this.pongBallStart(), layout);
    if (distance(point, ball) > 96) return false;
    session.pointer = { type: "aim", id, start: ball, current: point, subject: "pong" };
    return true;
  }

  private pointerDownWordHunt(session: Session, id: number, point: Point): boolean {
    const cell = this.cellAt(session, point);
    if (!cell) return false;
    session.pointer = { type: "word", id, path: [cell.index] };
    return true;
  }

  private releaseEightBallShot(session: Session): void {
    if (session.pointer.type !== "aim") return;
    const cue = this.cueBall(session);
    if (!cue) return;
    const pull = {
      x: session.pointer.start.x - session.pointer.current.x,
      y: session.pointer.start.y - session.pointer.current.y,
    };
    const power = Math.min(1, Math.hypot(pull.x, pull.y) / 185);
    if (power < 0.05) return;
    const layout = this.layout(POOL_W, POOL_H);
    const velocity = {
      x: (pull.x / Math.max(1, Math.hypot(pull.x, pull.y))) * power * 18 / layout.scale,
      y: (pull.y / Math.max(1, Math.hypot(pull.x, pull.y))) * power * 18 / layout.scale,
    };
    cue.vx = velocity.x;
    cue.vy = velocity.y;
    session.eightball!.lastShotAt = performance.now();
    session.message = "Rolling";
    this.context().playTap(0.045);
    this.context().sendEvent({
      gameId: "eightball",
      round: session.round,
      kind: "eightball_shot",
      shooterId: this.context().localPlayerId,
      start: { x: cue.x, y: cue.y },
      velocity,
    });
  }

  private releaseCupPongShot(session: Session): void {
    if (session.pointer.type !== "aim" || !session.cuppong) return;
    const layout = this.layout(VIRTUAL_W, VIRTUAL_H);
    const start = this.pongBallStart();
    const pull = {
      x: session.pointer.current.x - session.pointer.start.x,
      y: session.pointer.current.y - session.pointer.start.y,
    };
    const power = Math.min(1, Math.hypot(pull.x, pull.y) / 190);
    if (power < 0.06) return;
    const target = this.pongTargetFromPull(start, pull, power, layout.scale);
    const cup = this.pongCupAtTarget(session, target);
    const rimCup = cup ? undefined : this.pongRimCupAtTarget(session, target);
    if (cup) {
      session.score += 1;
      session.message = "Splash";
      this.context().playTap(0.06);
      this.context().updateScore(session.score, false);
    } else if (rimCup) {
      session.message = "Rim";
      this.context().playTap(0.035);
    } else {
      session.message = "Rim out";
      this.context().playTap(0.025);
    }
    this.addPongShot(session, start, target, cup?.id, rimCup?.id, this.localColor(), this.context().localPlayerId);
    this.context().sendEvent({
      gameId: "cuppong",
      round: session.round,
      kind: "cuppong_shot",
      shooterId: this.context().localPlayerId,
      start,
      target,
      hitCupId: cup?.id,
      rimCupId: rimCup?.id,
      score: session.score,
    });
    if (cup) cup.active = false;
    if (session.cuppong.cups.every((entry) => !entry.active)) {
      this.completeSession(session);
    }
  }

  private releaseWordPath(session: Session): void {
    if (session.pointer.type !== "word" || !session.wordhunt) return;
    const word = session.pointer.path.map((index) => session.wordhunt!.cells[index]?.letter ?? "").join("");
    const now = performance.now();
    if (word.length < 3) {
      session.message = "Too short";
      session.wordhunt.lastFeedback = {
        word,
        title: word || "Too short",
        detail: "Use 3+ connected tiles",
        kind: "warning",
        at: now,
      };
      return;
    }
    if (!WORDS.has(word)) {
      session.message = "Not in dictionary";
      session.wordhunt.lastFeedback = {
        word,
        title: word,
        detail: "Not in dictionary",
        kind: "error",
        at: now,
      };
      return;
    }
    if (session.wordhunt.found.has(word)) {
      session.message = "Already found";
      session.wordhunt.lastFeedback = {
        word,
        title: word,
        detail: "Already found",
        kind: "warning",
        at: now,
      };
      return;
    }
    const points = WORD_POINTS[word.length] ?? 2000 + (word.length - 6) * 700;
    session.wordhunt.found.add(word);
    session.wordhunt.foundOrder.push(word);
    session.wordhunt.lastWord = { word, points, at: now };
    session.wordhunt.lastFeedback = {
      word,
      title: word,
      detail: `+${points} pts`,
      kind: "success",
      points,
      at: now,
    };
    session.score += points;
    session.message = `+${points} ${word}`;
    this.context().playTap(0.045);
    this.context().updateScore(session.score, false);
    this.context().sendEvent({
      gameId: "wordhunt",
      round: session.round,
      kind: "wordhunt_word",
      playerId: this.context().localPlayerId,
      word,
      points,
      total: session.score,
      path: [...session.pointer.path],
    });
  }

  private extendWordPath(session: Session, point: Point): void {
    if (session.pointer.type !== "word") return;
    const cell = this.cellAt(session, point);
    if (!cell) return;
    const path = session.pointer.path;
    const current = path[path.length - 1];
    if (cell.index === current) return;
    if (path.length > 1 && cell.index === path[path.length - 2]) {
      path.pop();
      return;
    }
    if (path.includes(cell.index)) return;
    if (!areAdjacent(current, cell.index)) return;
    path.push(cell.index);
  }

  private pongTargetFromPull(start: Point, pull: Point, power: number, scale: number): Point {
    const backward = Math.max(0, pull.y);
    const horizontal = pull.x / scale;
    const launch = backward / scale;
    return {
      x: clamp(start.x - horizontal * (0.72 + power * 0.72), 150, 850),
      y: clamp(start.y - 160 - launch * (0.48 + power * 0.55), 106, 362),
    };
  }

  private pongCupAtTarget(session: Session, target: Point): Cup | undefined {
    return session.cuppong?.cups.find((cup) => {
      if (!cup.active) return false;
      const dx = (target.x - cup.x) / 34;
      const dy = (target.y - cup.y) / 26;
      return dx * dx + dy * dy <= 1;
    });
  }

  private pongRimCupAtTarget(session: Session, target: Point, ignoreCupId?: string): Cup | undefined {
    return session.cuppong?.cups.find((cup) => {
      if (!cup.active || cup.id === ignoreCupId) return false;
      const dx = (target.x - cup.x) / 58;
      const dy = (target.y - cup.y) / 44;
      return dx * dx + dy * dy <= 1;
    });
  }

  private addPongShot(
    session: Session,
    start: Point,
    target: Point,
    hitCupId: string | undefined,
    rimCupId: string | undefined,
    color: string,
    shooterId: string,
  ): void {
    session.cuppong?.shots.push({
      start,
      target,
      hitCupId,
      rimCupId,
      color,
      shooterId,
      startedAt: performance.now(),
      duration: PONG_DURATION,
      bounceAngle: this.pongBounceAngle(session, start, target, rimCupId),
    });
  }

  private pongBounceAngle(session: Session, start: Point, target: Point, rimCupId?: string): number {
    const rimCup = rimCupId ? session.cuppong?.cups.find((cup) => cup.id === rimCupId) : undefined;
    if (!rimCup) return Math.atan2(target.y - start.y, target.x - start.x);
    return Math.atan2(target.y - rimCup.y, target.x - rimCup.x);
  }

  private completeSession(session: Session): void {
    if (session.completed) return;
    session.completed = true;
    this.context().updateScore(session.score, true);
  }

  private drawArcadeBackdrop(ctx: CanvasRenderingContext2D, width: number, height: number, gameId: ArcadeGameId): void {
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    if (gameId === "eightball") {
      gradient.addColorStop(0, "#101a19");
      gradient.addColorStop(0.55, "#173f35");
      gradient.addColorStop(1, "#0b1214");
    } else if (gameId === "cuppong") {
      gradient.addColorStop(0, "#201c32");
      gradient.addColorStop(0.5, "#39435e");
      gradient.addColorStop(1, "#10141d");
    } else {
      gradient.addColorStop(0, "#12182a");
      gradient.addColorStop(0.5, "#233756");
      gradient.addColorStop(1, "#11131e");
    }
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = "rgba(255,255,255,0.06)";
    for (let x = -20; x < width + 40; x += 72) {
      for (let y = -20; y < height + 40; y += 72) {
        ctx.beginPath();
        ctx.arc(x, y, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  private drawEightBall(session: Session): void {
    const { ctx } = this.context();
    const layout = this.layout(POOL_W, POOL_H);
    ctx.save();
    ctx.translate(layout.x, layout.y);
    ctx.scale(layout.scale, layout.scale);
    this.roundRect(ctx, -36, -36, POOL_W + 72, POOL_H + 72, 32);
    ctx.fillStyle = "#3b2517";
    ctx.shadowColor = "rgba(0,0,0,0.45)";
    ctx.shadowBlur = 26;
    ctx.shadowOffsetY = 10;
    ctx.fill();
    ctx.shadowColor = "transparent";
    this.roundRect(ctx, POOL_RAIL, POOL_RAIL, POOL_W - POOL_RAIL * 2, POOL_H - POOL_RAIL * 2, 16);
    const felt = ctx.createLinearGradient(POOL_RAIL, POOL_RAIL, POOL_W - POOL_RAIL, POOL_H - POOL_RAIL);
    felt.addColorStop(0, "#1db77f");
    felt.addColorStop(1, "#08734f");
    ctx.fillStyle = felt;
    ctx.fill();
    ctx.strokeStyle = "rgba(211,180,106,0.9)";
    ctx.lineWidth = 6;
    ctx.stroke();
    for (const pocket of this.poolPockets()) {
      ctx.beginPath();
      ctx.arc(pocket.x, pocket.y, POOL_POCKET_VISUAL_RADIUS, 0, Math.PI * 2);
      ctx.fillStyle = "#050607";
      ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.16)";
      ctx.lineWidth = 5;
      ctx.stroke();
    }
    for (const ball of session.eightball?.balls ?? []) {
      if (ball.pocketed) continue;
      this.drawPoolBall(ctx, ball);
    }
    ctx.restore();
    this.drawAimLine(session);
    this.drawArcadeTitle("8-Ball", session.message, `${session.score} pts`);
  }

  private drawPoolBall(ctx: CanvasRenderingContext2D, ball: PoolBall): void {
    ctx.save();
    ctx.shadowColor = "rgba(0,0,0,0.35)";
    ctx.shadowBlur = 8;
    ctx.shadowOffsetY = 3;
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, ball.r, 0, Math.PI * 2);
    ctx.fillStyle = ball.color;
    ctx.fill();
    ctx.shadowColor = "transparent";
    if (ball.stripe) {
      ctx.save();
      ctx.clip();
      ctx.fillStyle = "#fff8e8";
      ctx.fillRect(ball.x - ball.r, ball.y - 6, ball.r * 2, 12);
      ctx.restore();
    }
    ctx.strokeStyle = "rgba(0,0,0,0.35)";
    ctx.lineWidth = 2;
    ctx.stroke();
    if (ball.label) {
      ctx.fillStyle = ball.id === "8" ? "#ffffff" : "#111318";
      ctx.font = `bold ${Math.max(16, ball.r * 0.78)}px Inter, system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(ball.label, ball.x, ball.y + 0.5);
    }
    ctx.restore();
  }

  private drawCupPong(session: Session): void {
    const { ctx } = this.context();
    const layout = this.layout(VIRTUAL_W, VIRTUAL_H);
    ctx.save();
    ctx.translate(layout.x, layout.y);
    ctx.scale(layout.scale, layout.scale);
    this.roundRect(ctx, 100, 45, 800, 610, 34);
    const table = ctx.createLinearGradient(100, 45, 900, 655);
    table.addColorStop(0, "#fbf4d0");
    table.addColorStop(0.5, "#ecb157");
    table.addColorStop(1, "#b96742");
    ctx.fillStyle = table;
    ctx.shadowColor = "rgba(0,0,0,0.28)";
    ctx.shadowBlur = 24;
    ctx.shadowOffsetY = 10;
    ctx.fill();
    ctx.shadowColor = "transparent";
    ctx.fillStyle = "rgba(255,255,255,0.2)";
    ctx.fillRect(120, 342, 760, 4);
    for (const cup of session.cuppong?.cups ?? []) {
      this.drawCup(ctx, cup);
    }
    const hasLocalShot = (session.cuppong?.shots ?? []).some((shot) => shot.shooterId === this.context().localPlayerId);
    if (!hasLocalShot) {
      const ball = this.pongBallStart();
      ctx.beginPath();
      ctx.arc(ball.x, ball.y, 22, 0, Math.PI * 2);
      ctx.fillStyle = "#ffffff";
      ctx.shadowColor = "rgba(0,0,0,0.24)";
      ctx.shadowBlur = 12;
      ctx.shadowOffsetY = 5;
      ctx.fill();
      ctx.shadowColor = "transparent";
    }
    for (const shot of session.cuppong?.shots ?? []) {
      this.drawPongShot(ctx, shot);
    }
    ctx.restore();
    this.drawAimLine(session);
    const cupsLeft = session.cuppong?.cups.filter((cup) => cup.active).length ?? 0;
    this.drawArcadeTitle("Cup Pong", session.message, `${session.score} hit${session.score === 1 ? "" : "s"} · ${cupsLeft} cups`);
  }

  private drawCup(ctx: CanvasRenderingContext2D, cup: Cup): void {
    ctx.save();
    ctx.globalAlpha = cup.active ? 1 : 0.24;
    ctx.beginPath();
    ctx.ellipse(cup.x, cup.y, 36, 28, 0, 0, Math.PI * 2);
    const fill = ctx.createRadialGradient(cup.x - 8, cup.y - 8, 3, cup.x, cup.y, 38);
    fill.addColorStop(0, "#fffcf4");
    fill.addColorStop(0.5, "#ef3c43");
    fill.addColorStop(1, "#87151f");
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.78)";
    ctx.lineWidth = 5;
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(cup.x, cup.y - 4, 22, 15, 0, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(30, 7, 11, 0.42)";
    ctx.fill();
    ctx.restore();
  }

  private drawPongShot(ctx: CanvasRenderingContext2D, shot: PongShot): void {
    const progress = clamp((performance.now() - shot.startedAt) / shot.duration, 0, 1);
    const rimBounce = Boolean(shot.rimCupId && !shot.hitCupId);
    const landingProgress = rimBounce ? 0.62 : 0.72;
    const flightProgress = shot.hitCupId ? progress : clamp(progress / landingProgress, 0, 1);
    let x = shot.start.x + (shot.target.x - shot.start.x) * flightProgress;
    let y = shot.start.y + (shot.target.y - shot.start.y) * flightProgress - Math.sin(flightProgress * Math.PI) * 185;
    let scale = 1 + Math.sin(flightProgress * Math.PI) * 0.72;
    if (!shot.hitCupId && progress > landingProgress) {
      const bounce = clamp((progress - landingProgress) / (1 - landingProgress), 0, 1);
      const roll = bounce * (rimBounce ? 112 : 86);
      const bounceHeight = Math.abs(Math.sin(bounce * Math.PI * (rimBounce ? 2.8 : 2.35))) * (1 - bounce) * (rimBounce ? 66 : 48);
      x = shot.target.x + Math.cos(shot.bounceAngle) * roll;
      y = shot.target.y + Math.sin(shot.bounceAngle) * roll - bounceHeight;
      x = clamp(x, 122, 878);
      y = clamp(y, 58, 650);
      scale = 0.86 + Math.abs(Math.sin(bounce * Math.PI * (rimBounce ? 2.8 : 2.35))) * 0.34;
    }
    ctx.save();
    ctx.globalAlpha = 0.28;
    ctx.strokeStyle = shot.color;
    ctx.lineWidth = 7;
    ctx.setLineDash([9, 13]);
    ctx.beginPath();
    ctx.moveTo(shot.start.x, shot.start.y);
    ctx.quadraticCurveTo((shot.start.x + shot.target.x) / 2, Math.min(shot.start.y, shot.target.y) - 190, shot.target.x, shot.target.y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.arc(x, y, 18 * scale, 0, Math.PI * 2);
    ctx.fillStyle = "#ffffff";
    ctx.shadowColor = shot.color;
    ctx.shadowBlur = 16;
    ctx.fill();
    if (!shot.hitCupId && progress > landingProgress) {
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 0.26 * (1 - progress);
      ctx.beginPath();
      ctx.ellipse(x, shot.target.y + 22, 22 * scale, 7 * scale, 0, 0, Math.PI * 2);
      ctx.fillStyle = "#1a140f";
      ctx.fill();
    }
    ctx.restore();
  }

  private drawWordHunt(session: Session): void {
    const { ctx } = this.context();
    const layout = this.layout(VIRTUAL_W, VIRTUAL_H);
    const wordhunt = session.wordhunt;
    if (!wordhunt) return;
    ctx.save();
    ctx.translate(layout.x, layout.y);
    ctx.scale(layout.scale, layout.scale);
    const remaining = Math.max(0, Math.ceil(this.wordRemainingMs(session) / 1000));
    ctx.fillStyle = "rgba(255,255,255,0.08)";
    this.roundRect(ctx, 166, 82, 668, 510, 30);
    ctx.fill();
    this.drawWordPath(ctx, session);
    for (const cell of wordhunt.cells) {
      const selected = session.pointer.type === "word" && session.pointer.path.includes(cell.index);
      ctx.save();
      ctx.translate(cell.x, cell.y);
      this.roundRect(ctx, -54, -42, 108, 84, 18);
      const fill = ctx.createLinearGradient(-54, -42, 54, 42);
      fill.addColorStop(0, selected ? "#ecf8ff" : "#ffffff");
      fill.addColorStop(1, selected ? "#6ee3ff" : "#c9d6ff");
      ctx.fillStyle = fill;
      ctx.shadowColor = selected ? "rgba(91,225,255,0.5)" : "rgba(0,0,0,0.22)";
      ctx.shadowBlur = selected ? 20 : 12;
      ctx.shadowOffsetY = 5;
      ctx.fill();
      ctx.shadowColor = "transparent";
      ctx.strokeStyle = selected ? "#ffffff" : "rgba(29,52,89,0.28)";
      ctx.lineWidth = selected ? 5 : 3;
      ctx.stroke();
      ctx.fillStyle = "#10203b";
      ctx.font = "900 42px Inter, system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(cell.letter, 0, 2);
      ctx.restore();
    }
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.font = "800 34px Inter, system-ui, sans-serif";
    ctx.textAlign = "center";
    this.drawCurrentWordPreview(ctx, session);
    this.drawFoundWordsTray(ctx, session);
    ctx.fillText(`${remaining}s`, 500, 682);
    this.drawWordFeedbackCallout(ctx, session);
    ctx.restore();
    const found = wordhunt.foundOrder.slice(-3).join(" · ") || "Find 3+ letter words";
    this.drawArcadeTitle("Word Hunt", session.message || found, `${session.score} pts`);
  }

  private drawCurrentWordPreview(ctx: CanvasRenderingContext2D, session: Session): void {
    const word = this.currentWord(session);
    if (!word) return;
    const validLength = word.length >= 3;
    ctx.save();
    ctx.translate(500, 36);
    this.roundRect(ctx, -212, -31, 424, 62, 20);
    const fill = ctx.createLinearGradient(-212, -31, 212, 31);
    fill.addColorStop(0, "rgba(255,255,255,0.96)");
    fill.addColorStop(1, validLength ? "rgba(110,227,255,0.94)" : "rgba(211,219,238,0.94)");
    ctx.fillStyle = fill;
    ctx.shadowColor = validLength ? "rgba(99,238,255,0.28)" : "rgba(0,0,0,0.16)";
    ctx.shadowBlur = 18;
    ctx.shadowOffsetY = 8;
    ctx.fill();
    ctx.shadowColor = "transparent";
    ctx.strokeStyle = "rgba(255,255,255,0.78)";
    ctx.lineWidth = 3;
    ctx.stroke();
    ctx.fillStyle = "#10203b";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "900 28px Inter, system-ui, sans-serif";
    ctx.fillText(word, 0, -7);
    ctx.font = "900 13px Inter, system-ui, sans-serif";
    ctx.fillStyle = "rgba(16,32,59,0.64)";
    ctx.fillText(validLength ? "release to submit" : "keep swiping", 0, 17);
    ctx.restore();
  }

  private drawFoundWordsTray(ctx: CanvasRenderingContext2D, session: Session): void {
    const found = session.wordhunt?.foundOrder ?? [];
    ctx.save();
    ctx.translate(500, 614);
    this.roundRect(ctx, -336, -28, 672, 58, 20);
    ctx.fillStyle = "rgba(5, 12, 24, 0.34)";
    ctx.fill();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if (!found.length) {
      ctx.fillStyle = "rgba(255,255,255,0.62)";
      ctx.font = "800 18px Inter, system-ui, sans-serif";
      ctx.fillText("Found words show here", 0, 2);
      ctx.restore();
      return;
    }
    const visible = found.slice(-6);
    const hidden = Math.max(0, found.length - visible.length);
    const labels = hidden ? [`+${hidden}`, ...visible] : visible;
    const gap = 8;
    const widths = labels.map((word) => Math.max(76, Math.min(136, word.length * 18 + 32)));
    const totalWidth = widths.reduce((sum, width) => sum + width, 0) + gap * Math.max(0, widths.length - 1);
    let x = -totalWidth / 2;
    labels.forEach((word, index) => {
      const width = widths[index];
      this.roundRect(ctx, x, -20, width, 40, 18);
      ctx.fillStyle = index === labels.length - 1 ? "rgba(110, 227, 255, 0.92)" : "rgba(255,255,255,0.16)";
      ctx.fill();
      ctx.fillStyle = index === labels.length - 1 ? "#10203b" : "rgba(255,255,255,0.86)";
      ctx.font = "900 22px Inter, system-ui, sans-serif";
      ctx.fillText(word, x + width / 2, 1);
      x += width + gap;
    });
    ctx.restore();
  }

  private drawWordFeedbackCallout(ctx: CanvasRenderingContext2D, session: Session): void {
    const feedback = session.wordhunt?.lastFeedback;
    if (!feedback || session.pointer.type === "word") return;
    const age = performance.now() - feedback.at;
    const duration = 1300;
    if (age > duration) return;
    const progress = clamp(age / duration, 0, 1);
    const alpha = progress < 0.75 ? 1 : 1 - (progress - 0.75) / 0.25;
    const lift = Math.sin(progress * Math.PI) * 10;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.translate(500, 36 - lift);
    this.roundRect(ctx, -190, -30, 380, 60, 20);
    const fill = ctx.createLinearGradient(-190, -30, 190, 30);
    if (feedback.kind === "success") {
      fill.addColorStop(0, "#e9fbff");
      fill.addColorStop(0.55, "#6ee3ff");
      fill.addColorStop(1, "#ffe16b");
    } else if (feedback.kind === "warning") {
      fill.addColorStop(0, "#fff8de");
      fill.addColorStop(0.62, "#ffd45c");
      fill.addColorStop(1, "#ff9f5c");
    } else {
      fill.addColorStop(0, "#ffe7ec");
      fill.addColorStop(0.62, "#ff7a98");
      fill.addColorStop(1, "#ffcf5c");
    }
    ctx.fillStyle = fill;
    ctx.shadowColor = feedback.kind === "success" ? "rgba(99, 238, 255, 0.34)" : "rgba(255, 126, 126, 0.26)";
    ctx.shadowBlur = 24;
    ctx.shadowOffsetY = 8;
    ctx.fill();
    ctx.shadowColor = "transparent";
    ctx.strokeStyle = "rgba(255,255,255,0.8)";
    ctx.lineWidth = 4;
    ctx.stroke();
    ctx.fillStyle = "#10203b";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "900 28px Inter, system-ui, sans-serif";
    ctx.fillText(feedback.title, 0, -6);
    ctx.font = "900 15px Inter, system-ui, sans-serif";
    ctx.fillStyle = "rgba(16,32,59,0.72)";
    ctx.fillText(feedback.detail, 0, 17);
    ctx.restore();
  }

  private drawWordPath(ctx: CanvasRenderingContext2D, session: Session): void {
    if (session.pointer.type !== "word" || !session.wordhunt || session.pointer.path.length < 2) return;
    ctx.save();
    ctx.strokeStyle = "rgba(99, 238, 255, 0.72)";
    ctx.lineWidth = 15;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    session.pointer.path.forEach((index, pathIndex) => {
      const cell = session.wordhunt!.cells[index];
      if (pathIndex === 0) ctx.moveTo(cell.x, cell.y);
      else ctx.lineTo(cell.x, cell.y);
    });
    ctx.stroke();
    ctx.restore();
  }

  private drawAimLine(session: Session): void {
    if (session.pointer.type !== "aim") return;
    const { ctx } = this.context();
    if (session.pointer.subject === "pong") {
      this.drawPongAimLine(session, ctx);
      return;
    }
    const pull = {
      x: session.pointer.start.x - session.pointer.current.x,
      y: session.pointer.start.y - session.pointer.current.y,
    };
    const power = clamp(Math.hypot(pull.x, pull.y) / (session.pointer.subject === "cue" ? 185 : 210), 0, 1);
    const length = 64 + power * 230;
    const mag = Math.hypot(pull.x, pull.y) || 1;
    const end = {
      x: session.pointer.start.x + (pull.x / mag) * length,
      y: session.pointer.start.y + (pull.y / mag) * length,
    };
    ctx.save();
    ctx.strokeStyle = "rgba(255,255,255,0.88)";
    ctx.lineWidth = 8;
    ctx.setLineDash([6, 13]);
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(session.pointer.start.x, session.pointer.start.y);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = "rgba(0,0,0,0.28)";
    ctx.lineWidth = 4;
    ctx.beginPath();
    ctx.moveTo(session.pointer.start.x, session.pointer.start.y);
    ctx.lineTo(session.pointer.current.x, session.pointer.current.y);
    ctx.stroke();
    ctx.restore();
  }

  private drawPongAimLine(session: Session, ctx: CanvasRenderingContext2D): void {
    if (session.pointer.type !== "aim") return;
    const layout = this.layout(VIRTUAL_W, VIRTUAL_H);
    const startVirtual = this.pongBallStart();
    const pull = {
      x: session.pointer.current.x - session.pointer.start.x,
      y: session.pointer.current.y - session.pointer.start.y,
    };
    const power = clamp(Math.hypot(pull.x, pull.y) / 190, 0, 1);
    const target = this.pongTargetFromPull(startVirtual, pull, power, layout.scale);
    const start = toScreen(startVirtual, layout);
    const end = toScreen(target, layout);
    const control = {
      x: (start.x + end.x) / 2,
      y: Math.min(start.y, end.y) - (132 + power * 84) * layout.scale,
    };
    ctx.save();
    ctx.strokeStyle = "rgba(255,255,255,0.88)";
    ctx.lineWidth = 7;
    ctx.setLineDash([7, 12]);
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.quadraticCurveTo(control.x, control.y, end.x, end.y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.strokeStyle = "rgba(0,0,0,0.34)";
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(session.pointer.current.x, session.pointer.current.y);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(end.x, end.y, 18 + power * 8, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(110,227,255,0.9)";
    ctx.lineWidth = 4;
    ctx.stroke();
    ctx.restore();
  }

  private drawArcadeTitle(title: string, message: string, score: string): void {
    const { canvas, ctx } = this.context();
    const rect = canvas.getBoundingClientRect();
    ctx.save();
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillStyle = "rgba(5,8,13,0.52)";
    this.roundRect(ctx, 18, rect.height - 82, Math.min(rect.width - 36, 520), 60, 18);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = "900 18px Inter, system-ui, sans-serif";
    ctx.fillText(title, 38, rect.height - 70);
    ctx.font = "700 13px Inter, system-ui, sans-serif";
    ctx.fillStyle = "rgba(255,255,255,0.74)";
    ctx.fillText(message, 38, rect.height - 44);
    ctx.textAlign = "right";
    ctx.fillStyle = "#f5d44f";
    ctx.font = "900 17px Inter, system-ui, sans-serif";
    ctx.fillText(score, Math.min(rect.width - 38, 520), rect.height - 58);
    ctx.restore();
  }

  private cueBall(session: Session): PoolBall | undefined {
    return session.eightball?.balls.find((ball) => ball.id === "cue");
  }

  private poolMoving(session: Session): boolean {
    return Boolean(session.eightball?.balls.some((ball) => !ball.pocketed && Math.hypot(ball.vx, ball.vy) > 0.08));
  }

  private poolPockets(): Point[] {
    const corner = POOL_RAIL;
    return [
      { x: corner, y: corner },
      { x: POOL_W / 2, y: POOL_RAIL },
      { x: POOL_W - corner, y: corner },
      { x: corner, y: POOL_H - corner },
      { x: POOL_W / 2, y: POOL_H - POOL_RAIL },
      { x: POOL_W - corner, y: POOL_H - corner },
    ];
  }

  private pongBallStart(): Point {
    const slot = this.context().localSlot ?? "A";
    const offset = slot === "B" ? 48 : slot === "C" ? -48 : slot === "D" ? 96 : 0;
    return { x: VIRTUAL_W / 2 + offset, y: 602 };
  }

  private cellAt(session: Session, screenPoint: Point): WordCell | undefined {
    const layout = this.layout(VIRTUAL_W, VIRTUAL_H);
    const point = toVirtual(screenPoint, layout);
    return session.wordhunt?.cells.find((cell) => Math.abs(cell.x - point.x) <= 58 && Math.abs(cell.y - point.y) <= 46);
  }

  private currentWord(session: Session): string {
    if (session.pointer.type !== "word" || !session.wordhunt) return "";
    return session.pointer.path.map((index) => session.wordhunt!.cells[index]?.letter ?? "").join("");
  }

  private wordRemainingMs(session: Session): number {
    if (!session.wordhunt) return 0;
    return Math.max(0, session.wordhunt.durationMs - (performance.now() - session.wordhunt.startedAt));
  }

  private forcedWordBoardIndex(): number | undefined {
    const raw = sessionStorage.getItem("pigeon-putt:test-word-board") ?? new URLSearchParams(location.search).get("wordBoard");
    if (raw === null) return undefined;
    const index = Number(raw);
    return Number.isInteger(index) && index >= 0 && index < WORD_BOARDS.length ? index : undefined;
  }

  private eventPoint(event: PointerEvent): Point {
    const rect = this.context().canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  private layout(virtualWidth: number, virtualHeight: number): Layout {
    const rect = this.context().canvas.getBoundingClientRect();
    const safeTop = rect.width < 620 ? 122 : 92;
    const safeBottom = rect.width < 620 ? 92 : 74;
    const scale = Math.min((rect.width - 32) / virtualWidth, (rect.height - safeTop - safeBottom) / virtualHeight);
    const w = virtualWidth * scale;
    const h = virtualHeight * scale;
    const freeY = Math.max(0, rect.height - safeTop - safeBottom - h);
    const isMobileWideGame = rect.width < 620 && virtualWidth / virtualHeight > 1.55;
    return {
      x: (rect.width - w) / 2,
      y: safeTop + (isMobileWideGame ? Math.min(92, freeY * 0.28) : freeY / 2),
      w,
      h,
      scale,
    };
  }

  private playerName(id: string): string {
    return this.players.find((player) => player.id === id)?.name ?? "Player";
  }

  private playerColor(id: string): string {
    return this.players.find((player) => player.id === id)?.color ?? "#ffffff";
  }

  private localColor(): string {
    return SLOT_COLORS[this.context().localSlot ?? "A"];
  }

  private roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, radius: number): void {
    const r = Math.min(radius, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }
}

function toScreen(point: Point, layout: Layout): Point {
  return { x: layout.x + point.x * layout.scale, y: layout.y + point.y * layout.scale };
}

function toVirtual(point: Point, layout: Layout): Point {
  return { x: (point.x - layout.x) / layout.scale, y: (point.y - layout.y) / layout.scale };
}

function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function areAdjacent(a: number, b: number): boolean {
  const ax = a % 4;
  const ay = Math.floor(a / 4);
  const bx = b % 4;
  const by = Math.floor(b / 4);
  return Math.max(Math.abs(ax - bx), Math.abs(ay - by)) === 1;
}

function seededRandom(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
